import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { TrustBadge } from "@/components/common/TrustBadge";
import { Search, Database, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Data provenance — where the information on this screen is drawn from.
 * Source-of-truth is the Consumer Intelligence Profile, which is continuously
 * enriched by events emitted from the institution's existing enterprise systems
 * via the Integration Framework. Altara never replaces these systems — it
 * consumes their events and updates the profile.
 *
 * The list below mirrors the connector catalog on the Integrations page and the
 * categories defined in the Consumer Trust Infrastructure architecture.
 */
const DATA_SOURCES = [
  { category: "CRM", examples: "Salesforce · Microsoft Dynamics · HubSpot · SAP CRM" },
  { category: "Core Banking", examples: "Temenos · Mambu · Finacle · Oracle FLEXCUBE · Finastra · Backbase" },
  { category: "Insurance", examples: "Guidewire · Sapiens · Duck Creek · FINEOS" },
  { category: "Learning Platforms", examples: "Altara Consumer Education · Moodle · Cornerstone · SAP SuccessFactors · Docebo · SCORM · xAPI" },
  { category: "Customer Support", examples: "Genesys · Amazon Connect · Zendesk · Freshdesk · Microsoft Dynamics" },
  { category: "Complaints", examples: "Salesforce Service Cloud · Dynamics Customer Service · Zendesk · Custom REST APIs" },
  { category: "Identity", examples: "Microsoft Entra · Okta · Keycloak · Auth0" },
  { category: "Document Management", examples: "SharePoint · OpenText · Alfresco · Box" },
  { category: "Notification Platforms", examples: "Microsoft Graph · Twilio · SendGrid · Firebase · WhatsApp Business API" },
];

export default function Consumers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/consumers", { params: q ? { search: q } : {} })
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  }, [q]);

  const totals = useMemo(() => {
    const bands = { at_risk: 0, watch: 0, healthy: 0, strong: 0 };
    rows.forEach(r => { bands[r.trust_band] = (bands[r.trust_band] || 0) + 1; });
    return bands;
  }, [rows]);

  return (
    <div data-testid={TID.consumersPage} className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Consumers</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Consumer register</h1>
          <p className="text-sm text-muted-foreground mt-1">Every consumer has one continuously evolving Consumer Intelligence Profile.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <BandTotal label="At risk / watch" value={(totals.at_risk || 0) + (totals.watch || 0)} tone="rose" />
          <BandTotal label="Healthy" value={totals.healthy || 0} tone="teal" />
          <BandTotal label="Strong" value={totals.strong || 0} tone="emerald" />
        </div>
      </header>

      {/* Data provenance — where the information on this screen is drawn from */}
      <section
        data-testid="consumers-data-provenance"
        className="border border-slate-200 bg-white rounded-sm"
      >
        <button
          type="button"
          onClick={() => setSourcesOpen(v => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
          data-testid="consumers-data-provenance-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-sm bg-teal-50 border border-teal-200 flex items-center justify-center shrink-0">
              <Database className="w-4 h-4 text-altara-teal" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Where this information comes from</div>
              <div className="text-sm text-slate-700 leading-snug">
                Consumer data is <strong className="text-altara-deep">not created by Altara</strong>. It is drawn from
                your institution&rsquo;s existing enterprise systems via the Integration Framework and continuously used
                to enrich the Consumer Intelligence Profile.
              </div>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 shrink-0 flex items-center gap-1">
            {sourcesOpen ? "Hide sources" : "Show sources"}
            {sourcesOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        </button>
        {sourcesOpen && (
          <div className="border-t border-slate-100 px-4 py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 bg-slate-50/40">
            {DATA_SOURCES.map(s => (
              <div key={s.category} className="border border-slate-200 rounded-sm bg-white p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-altara-teal font-semibold">{s.category}</div>
                <div className="text-xs text-slate-600 mt-1 leading-relaxed">{s.examples}</div>
              </div>
            ))}
            <div className="md:col-span-2 lg:col-span-3 text-[11px] text-slate-500 leading-relaxed pt-1">
              Source system events flow through the Integration Framework → Event Bus → Consumer Intelligence Engine.
              Every field displayed on this register is either directly observed from a source system or derived
              deterministically from observed evidence. Connector status is visible under <strong>Integrations</strong>.
            </div>
          </div>
        )}
      </section>

      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid={TID.consumersSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by consumer name…"
            className="pl-9 rounded-sm"
          />
        </div>
        <div className="text-[11px] font-mono text-muted-foreground">{rows.length} results</div>
      </div>

      <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
        <table data-testid={TID.consumersTable} className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Consumer</th>
              <th className="text-left px-4 py-2.5 font-medium">Segment</th>
              <th className="text-left px-4 py-2.5 font-medium">Products</th>
              <th className="text-left px-4 py-2.5 font-medium">Literacy</th>
              <th className="text-left px-4 py-2.5 font-medium">Trust</th>
              <th className="text-left px-4 py-2.5 font-medium">Open recs</th>
              <th className="text-left px-4 py-2.5 font-medium">Consumer ID</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.map(r => (
              <tr key={r.consumer_id} data-testid={TID.consumerRow} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-4 py-2.5">
                  <Link to={`/consumers/${r.consumer_id}`} className="font-medium text-altara-deep hover:text-altara-teal">
                    {r.identity.full_name}
                  </Link>
                  <div className="text-[10px] text-muted-foreground">{r.identity.email}</div>
                </td>
                <td className="px-4 py-2.5 capitalize text-slate-700">{r.relationship?.segment}</td>
                <td className="px-4 py-2.5 text-slate-700">{r.products?.length || 0}</td>
                <td className="px-4 py-2.5">
                  <span className="font-mono tabular-nums">{r.understanding?.literacy_score}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-1">{r.understanding?.literacy_band}</span>
                </td>
                <td className="px-4 py-2.5"><TrustBadge score={r.trust_score} band={r.trust_band} size="sm" /></td>
                <td className="px-4 py-2.5 font-mono tabular-nums">{r.counts?.open_recommendations || 0}</td>
                <td className="px-4 py-2.5 font-mono text-[10px] text-slate-500">{r.consumer_id}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No consumers match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BandTotal({ label, value, tone }) {
  const c = { rose: "text-rose-700 border-rose-200 bg-rose-50", teal: "text-teal-700 border-teal-200 bg-teal-50", emerald: "text-emerald-700 border-emerald-200 bg-emerald-50" }[tone];
  return (
    <div className={`px-2.5 py-1.5 border rounded-sm ${c}`}>
      <div className="text-[9px] uppercase tracking-[0.15em]">{label}</div>
      <div className="font-mono font-semibold text-sm tabular-nums">{value}</div>
    </div>
  );
}
